import { useEffect, useCallback, useRef } from 'react'

interface PopupConfig {
  url: string
  name?: string
  onError: (errorType: 'closed-early' | 'blocked') => void
}

export function usePopup() {
  const popupRef = useRef<Window>(null)
  const checkIntervalRef = useRef<number>(null)

  const close = useCallback(() => {
    if (popupRef.current) {
      popupRef.current.close()
      popupRef.current = null
    }
    if (checkIntervalRef.current) {
      window.clearInterval(checkIntervalRef.current)
      checkIntervalRef.current = null
    }
  }, [])

  const focus = useCallback(() => {
    popupRef.current?.focus()
  }, [])

  const open = useCallback(
    ({ url, name, onError }: PopupConfig) => {
      if (popupRef.current) {
        popupRef.current.focus()
        return
      }

      const popup = window.open(url, name)
      if (!popup) {
        onError('blocked')
        return
      }
      popupRef.current = popup

      checkIntervalRef.current = window.setInterval(() => {
        // this means that the popup was closed by the user
        // before the SSO flow was completed
        if (popupRef.current?.closed) {
          onError('closed-early')
          close()
        }
      }, 200)
    },
    [close],
  )

  useEffect(() => {
    // make sure to clean up any listeners when the component is unmounted
    return () => {
      close()
    }
  }, [close])

  return {
    open,
    close,
    focus,
  }
}
